import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { AbstractComponent, FocusableComponent } from '../../abstract.component';
import { SolSelect, SolSelectEdge, SolSelectSearchEvent, SolSelectPresentation } from '../../../models/core/sol-select';
import { SolSelectValue } from '../../../models/core/sol-select-value';
import { SolViewComponent } from '../../view/sol-view/sol-view.component';
import { SolIcon } from '@sol/icons';
import { SolIconComponent } from '../sol-icon/sol-icon.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolText } from '../../../models/core/sol-text';
import { SolTag } from '../../../models/core/sol-tag';
import { SolType } from '../../../models/core/sol-type';
import { SolViewId } from '../../../models/core/sol-view-id';
import { SolFieldType } from '../../../models/core/sol-text-field';
import { SolMenuGroup } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { ObjectTools } from '../../../tools/object-tools';
import { LocaleService } from '../../../services/locale.service';
import { PopoverService, SolPopoverRef } from '../../../services/popover.service';
import { DialogService } from '../../../services/dialog.service';
import { SolSelectAddValueDialogComponent } from '../../dialog/sol-select-add-value-dialog/sol-select-add-value-dialog.component';
import { BrowserTools } from '../../../tools/browser-tools';
import { SelectTools } from '../../../tools/select-tools';
import {
  SolSelectContentCallback,
  SolSelectContentComponent,
} from '../sol-select-content/sol-select-content.component';

@Component({
  selector: 'sol-select',
  standalone: true,
  imports: [SolViewComponent, SolIconComponent, TranslatePipe, SolSelectContentComponent],
  templateUrl: './sol-select.component.html',
})
export class SolSelectComponent<V = SolText> extends AbstractComponent<SolSelect<V>> implements OnDestroy {
  protected readonly SolIcon = SolIcon;

  // Dependencies
  private readonly popoverService = inject(PopoverService);
  private readonly localeService = inject(LocaleService);
  private readonly dialogService = inject(DialogService);

  // Computed
  protected readonly select$ = this.compute(state => state);
  protected readonly embedded$ = this.compute(state => state.presentation === 'embedded');
  protected readonly selectedViewData$ = this.compute(state => state.selectedViewData);
  protected readonly hasSelection$ = this.compute(state => state.hasSelection);
  protected readonly placeholder$ = this.compute(state => state.placeholder);
  protected readonly disabled$ = this.compute(state => state.disabled);
  protected readonly hasError$ = this.compute(state => state.hasError);
  protected readonly loading$ = this.compute(state => state.loading);
  protected readonly transparent$ = this.compute(state => state.transparent);
  protected readonly popoverVisible$ = this.compute(state => state.popoverVisible);
  protected readonly listboxId$ = this.compute(state => state.listboxId);
  protected readonly activeOptionId$ = this.compute(state => state.activeOptionId);

  // Events
  @Output() public readonly finish = new EventEmitter<void>();
  @Output() public readonly dismiss = new EventEmitter<void>();
  @Output('select')
  public selectEmitter = new EventEmitter<SolSelect<V>>();
  @Output('value')
  public valueEmitter = new EventEmitter<string | undefined>();
  @Output('values')
  public valuesEmitter = new EventEmitter<(string | undefined)[]>();
  @Output('search')
  public searchEmitter = new EventEmitter<SolSelectSearchEvent>();
  @Output('clear')
  public clearEmitter = new EventEmitter<void>();
  @Output('focus')
  public focusEmitter = new EventEmitter<boolean>();
  @Output('blur')
  public blurEmitter = new EventEmitter<boolean>();
  @Output('focused')
  public focusedEmitter = new EventEmitter<boolean>();
  @Output('menuAction')
  public menuActionEmitter = new EventEmitter<string>();

  protected readonly contentCallback: SolSelectContentCallback = {
    onSearch: query => this.handleSearch(query),
    onSelect: (id, close) => this.handleSelect(id, close),
    onMoveHighlight: step => this.handleMoveHighlight(step),
    onEnter: () => this.handleEnter(),
    onEscape: () => this.handleEscape(),
    onClear: () => this.handleClear(),
    onAddValue: () => this.handleAddValue(),
    onMenuAction: actionId => this.handleMenuAction(actionId),
  };

  // Binding
  @ViewChild('embeddedContent')
  private embeddedContent?: SolSelectContentComponent<V>;
  @ViewChild('container')
  private containerRef?: ElementRef<HTMLDivElement>;

  // Cache
  private popoverRef?: SolPopoverRef<SolSelectContentComponent<any>>;
  private destroyed = false;
  private restoreFocusOnClose = true;

  constructor() {
    super(SolSelect.ofScalar<any>(SolType.text, SolViewId.text));

    this.watchComputed(
      state => state.popoverVisible,
      () => this.syncPopover(),
    );
    this.watchComputed(
      state => state,
      () => this.syncPopover(),
    );
    this.localeService.watchComputed(
      state => state.translationsLocale,
      () => this.refreshLabels(),
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closePopoverRef();
  }

  @Input()
  public set select(select: SolSelect<V>) {
    this.patch(this.withSearchLabels(select).calculateViewData());
  }

  @Input()
  public set presentation(presentation: SolSelectPresentation) {
    this.update(state => state.withPresentation(presentation));
  }

  @Input()
  public set value(value: string | undefined) {
    this.update(state => state.withValues([value]));
  }

  @Input()
  public set values(values: (string | undefined)[]) {
    this.update(state => state.withValues(values));
  }

  @Input()
  public set availableValues(availableValues: SolSelectValue<V>[]) {
    this.update(state => this.withSearchLabels(state.withAvailableValues(availableValues)));
  }

  @Input()
  public set multi(multi: boolean) {
    this.update(state => state.withMode(multi ? 'multi' : 'single'));
  }

  @Input()
  public set disabled(disabled: boolean) {
    this.update(state => state.withDisabled(disabled));
  }

  @Input()
  public set hasError(hasError: boolean) {
    this.update(state => state.withHasError(hasError));
  }

  @Input()
  public set editable(editable: boolean) {
    this.update(state => state.withEditable(editable));
  }

  @Input()
  public set searchable(searchable: boolean) {
    this.update(state => state.withSearchable(searchable));
  }

  @Input()
  public set grow(grow: boolean) {
    this.update(state => state.withGrow(grow));
  }

  @Input()
  public set placeholder(placeholder: string) {
    this.update(state => state.withPlaceholder(SolText.of(placeholder)));
  }

  @Input()
  public set type(type: SolFieldType) {
    this.update(state => state.withTransparent(type === SolFieldType.transparent));
  }

  @Input()
  public set menu(menu: SolMenuGroup[]) {
    this.update(state => state.withMenu(menu));
  }

  protected onClick(): void {
    const select = this.state.withClickField();
    this.onSelect(select);
  }

  protected onArrowKeyDown(step: 1 | -1, event: Event): void {
    event.preventDefault();
    this.handleMoveHighlight(step);
  }

  protected onEnterKey(event: Event): void {
    event.preventDefault();
    this.handleEnter();
  }

  protected onSpaceKey(event: Event): void {
    event.preventDefault();
    this.handleSpace();
  }

  /** Escape is the one key `data-shortcut-ignore=""` leaves to the layer walk, so consuming it here without stopping propagation dismisses the dialog underneath too. */
  protected onEscapeKey(event: Event): void {
    const consumed = this.state.popoverVisible;
    event.preventDefault();
    if (consumed) {
      event.stopPropagation();
    }
    this.handleEscape();
  }

  protected onEdgeKey(edge: SolSelectEdge, event: Event): void {
    event.preventDefault();
    this.onSelect(this.state.withEdgeMove(edge));
  }

  protected onFocusIn(): void {
    this.focusEmitter.emit(true);
    this.focusedEmitter.emit(true);
  }

  protected override focusable(): FocusableComponent | undefined {
    return this.embeddedContent ?? BrowserTools.toFocusable(this.containerRef);
  }

  private openPopover(): void {
    if (this.popoverRef !== undefined) {
      return;
    }
    this.restoreFocusOnClose = true;
    const select = this.state;
    this.popoverRef = this.popoverService.show(
      SolSelectContentComponent,
      (componentRef, id) => {
        componentRef.instance.select = select.withListboxId(SelectTools.toListboxId(id)) as SolSelect<any>;
        componentRef.instance.callback = this.contentCallback;
      },
      {
        width: select.grow ? 'parent-grow' : 'parent',
        boundsRef: this.containerRef,
        clickOutside: () => this.closePopover(false),
        afterClose: () => this.onPopoverClosed(),
      },
    );
    this.onSelect(this.state.withListboxId(SelectTools.toListboxId(this.popoverRef.id)));
  }

  private closePopover(restoreFocus: boolean): void {
    this.restoreFocusOnClose = restoreFocus;
    this.setPopoverVisible(false);
  }

  private closePopoverRef(): void {
    this.popoverRef?.close();
    this.popoverRef = undefined;
    if (this.state.listboxId !== undefined) {
      this.onSelect(this.state.withListboxId(undefined));
    }
  }

  private onPopoverClosed(): void {
    this.popoverRef = undefined;
    this.setPopoverVisible(false);
    if (this.restoreFocusOnClose) {
      queueMicrotask(() => this.restoreFocusToTrigger());
    }
    this.blurEmitter.emit(false);
    this.focusedEmitter.emit(false);
  }

  /**
   * The search field the popover took focus into is gone once it closes, so focus would land on the
   * document. Hand it back to the trigger the user opened — but never while tearing down, where
   * taking focus would steal it from whatever replaces us, and never after a click outside, which
   * would yank focus off the element the user just clicked. Deferred, because `afterClose` runs
   * before the popover element is removed and its focus trap disposed, and that teardown moves
   * focus last.
   */
  private restoreFocusToTrigger(): void {
    const container = this.containerRef?.nativeElement;
    if (this.destroyed || container === undefined || !container.isConnected) {
      return;
    }
    if (container.contains(document.activeElement)) {
      return;
    }
    container.focus();
  }

  private handleSearch(query: string): void {
    if (this.popoverRef === undefined && this.state.presentation === 'dropdown') {
      return;
    }
    const state = this.state;
    const oldQuery = state.query;
    const select = state.withSearch(query);
    this.onSelect(select);
    if (select.canSearch() && query !== oldQuery) {
      this.searchEmitter.emit({ query, searchVersion: select.searchVersion });
    }
  }

  private handleSelect(id: string | undefined, close: boolean): void {
    const complete = close && this.state.completesSelection(id);
    const select = this.state.withSelect(id, close);
    this.onSelect(select);
    if (complete) {
      this.finish.emit();
    }
  }

  private handleMoveHighlight(step: 1 | -1): void {
    const select = this.state.withArrowMove(step);
    this.onSelect(select);
  }

  private handleEnter(): void {
    const complete = this.state.completesSelection(this.state.currentResultId);
    const select = this.state.withEnter();
    this.onSelect(select);
    if (complete) {
      this.finish.emit();
    }
  }

  private handleSpace(): void {
    const select = this.state.withSpace();
    this.onSelect(select);
  }

  private handleEscape(): void {
    const select = this.state.withEscape();
    this.onSelect(select);
    if (this.state.presentation === 'embedded') {
      this.dismiss.emit();
    }
  }

  private handleClear(): void {
    const select = this.state.withClearSelection();
    const previous = this.state;
    this.onSelect(select);
    if (select.values !== previous.values) {
      this.clearEmitter.emit();
    }
  }

  /**
   * The popover has to go before the dialog opens, or the dialog lands under a listbox that is still
   * claiming outside clicks. Its focus is not restored to the trigger either — the dialog is taking
   * focus, and a restore would land after it and pull focus back out of the form.
   */
  private handleAddValue(): void {
    this.closePopover(false);
    this.dialogService.showDialog<SolSelectAddValueDialogComponent>(SolSelectAddValueDialogComponent, instance => {
      instance.onValueAdded = value => this.onSelect(this.state.withAddedValue(value));
    });
  }

  private handleMenuAction(actionId: string): void {
    this.menuActionEmitter.emit(actionId);
    this.closePopover(true);
  }

  private refreshLabels(): void {
    this.patch(this.withSearchLabels(this.state));
  }

  private withSearchLabels(select: SolSelect<V>): SolSelect<V> {
    return select.withSearchLabels(select.availableValues.map(value => this.withSearchLabel(value)));
  }

  /** Returns the option itself when the label already matches: `ObjectTools.equals` counts keys, so spreading in a `searchLabel: undefined` the consumer's literal never had would stop `withSearchLabels` ever short-circuiting. */
  private withSearchLabel(value: SolSelectValue<V>): SolSelectValue<V> {
    const searchLabel = this.toSearchLabel(value);
    if (searchLabel === value.searchLabel) {
      return value;
    }
    return { ...value, searchLabel };
  }

  /** The option's hint joins its label, so a row is found by any text it shows. Without a label there is nothing to fold into and the filter's own id fallback stands. */
  private toSearchLabel(value: SolSelectValue<V>): string | undefined {
    const label = this.toLabel(value.name);
    const info = this.toLabel(value.info);
    if (label === undefined || info === undefined || info.length === 0) {
      return label;
    }
    return `${label} ${info}`;
  }

  private toLabel(value: unknown): string | undefined {
    if (value instanceof SolTag) {
      return this.toLabel(value.text);
    }
    if (value instanceof SolText) {
      return this.localeService.translateText(value);
    }
    if (typeof value === 'string') {
      return value;
    }
    return undefined;
  }

  private onSelect(select: SolSelect<V>): void {
    const previous = this.state;
    if (select === previous) {
      return;
    }
    this.patch(select);
    this.selectEmitter.emit(select);
    this.emitValues(previous, select);
  }

  /** Silent while the selection is unchanged: consumers re-enter their own state from these and rely on not being called for a no-op. */
  private emitValues(previous: SolSelect<V>, select: SolSelect<V>): void {
    if (ObjectTools.equals(previous.values, select.values)) {
      return;
    }
    if (select.mode === 'multi') {
      this.valuesEmitter.emit(select.values);
      return;
    }
    this.valueEmitter.emit(select.value());
  }

  private setPopoverVisible(visible: boolean): void {
    const select = this.state.withPopoverVisible(visible);
    this.onSelect(select);
  }

  private syncPopover(): void {
    const select = this.state;
    if (select.presentation === 'embedded' || !select.popoverVisible) {
      this.closePopoverRef();
      return;
    }
    if (this.popoverRef === undefined) {
      this.openPopover();
      return;
    }
    const instance = this.popoverRef.instance();
    if (instance !== undefined) {
      instance.select = select as SolSelect<any>;
    }
  }
}
